'use client'

import { Button } from '@/components/ui/button'
import { useTranslations } from 'next-intl'
import { REACTION_EMOJIS, type ReactionType } from '@/types'

const REACTION_TYPES: ReactionType[] = ['love', 'fire', 'applause', 'wow', 'heart_eyes', 'melting']

const REACTION_COLORS: Record<string, string> = {
  love: 'border-red-500/30 bg-red-500/10 hover:bg-red-500/20',
  fire: 'border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20',
  applause: 'border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20',
  wow: 'border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20',
  heart_eyes: 'border-pink-500/30 bg-pink-500/10 hover:bg-pink-500/20',
  melting: 'border-lime-500/30 bg-lime-500/10 hover:bg-lime-500/20',
}

interface ReactionBarProps {
  visitorReactionTokens: number
  visitorHasTipped: boolean
  onReaction: (type: ReactionType) => void
}

export function ReactionBar({ visitorReactionTokens, visitorHasTipped, onReaction }: ReactionBarProps) {
  const t = useTranslations('demo')
  const maxReactions = visitorHasTipped ? 10 : 3

  return (
    <div className="space-y-3" data-coach="send-reaction">
      {/* Reaction tokens indicator */}
      <div className="flex items-center justify-center gap-1">
        {Array.from({ length: maxReactions }).map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i < visitorReactionTokens
                ? 'bg-gradient-to-r from-pink-500 to-violet-500'
                : 'bg-zinc-700'
            }`}
          />
        ))}
        <span className="text-xs text-zinc-500 ml-1">
          {visitorReactionTokens}/{maxReactions}
          {!visitorHasTipped && <span className="text-amber-400 ml-1">&bull; {t('tipForMore')}</span>}
        </span>
      </div>

      {/* Low tokens prompt */}
      {visitorReactionTokens === 0 && (
        <p className="text-xs text-center text-zinc-500">
          {t('reactionsRefill')} {!visitorHasTipped && `• ${t('tippersRefillFaster')}`}
        </p>
      )}

      {/* Reaction buttons */}
      <div className="flex items-center justify-center gap-2">
        {REACTION_TYPES.map(type => (
          <Button
            key={type}
            variant="outline"
            size="icon"
            aria-label={`React with ${type.replace('_', ' ')}`}
            onClick={() => onReaction(type)}
            disabled={visitorReactionTokens <= 0}
            className={`${REACTION_COLORS[type]} text-lg disabled:opacity-40 disabled:cursor-not-allowed h-10 w-10`}
          >
            {REACTION_EMOJIS[type]}
          </Button>
        ))}
      </div>
    </div>
  )
}
